// Re-score a snapshot locally (same engine as the server) so votes feel instant.
import type { CandidateView, MeetupSnapshot } from '../shared/schemas.ts';
import {
  areasOf,
  focusOf,
  type Person,
  type Remembered,
  scoreCandidates,
  type TimeTable,
  timeKey,
  type VoteRow,
} from '../src/domain/engine.ts';
import type { Policy } from '../src/domain/feynman.ts';

export function applyLocal(s: MeetupSnapshot, meId: string, mine: Map<string, number>): MeetupSnapshot {
  if (!mine.size) return s;
  const votes: VoteRow[] = [];
  for (const c of s.candidates) {
    for (const m of c.up) votes.push({ memberId: m, candidateId: c.id, value: 1 });
    for (const m of c.down) votes.push({ memberId: m, candidateId: c.id, value: -1 });
  }
  const kept = votes.filter((x) => !(x.memberId === meId && mine.has(x.candidateId)));
  for (const [cid, value] of mine) if (value) kept.push({ memberId: meId, candidateId: cid, value });
  const people: Person[] = s.members.map((m) => ({ id: m.id, lat: m.lat, lon: m.lon, mode: m.mode }));
  const table: TimeTable = new Map();
  for (const c of s.candidates) for (const [mid, sec] of Object.entries(c.times)) table.set(timeKey(mid, c.id), sec);
  const known = s.candidates.filter((c) => c.visited).map((c) => ({ lat: c.lat, lon: c.lon, extId: c.extId }));
  const memory: Remembered[] = s.candidates.flatMap((c) =>
    c.memory ? [{ lat: c.lat, lon: c.lon, extId: c.extId, ...c.memory }] : [],
  );
  const f = s.feynman;
  const policy: Policy = f
    ? { stance: f.stance, n: f.n, threshold: f.threshold, best: { ...f.best, extId: null }, known }
    : { stance: 'explore', n: 1, threshold: 1, best: null, known };
  const keep = new Map(s.candidates.map((c) => [c.id, c]));
  const raw = scoreCandidates<CandidateView>(people, s.candidates, table, kept, { memory, policy });
  const scored: CandidateView[] = raw.map((c) => ({
    ...c,
    estimated: keep.get(c.id)?.estimated ?? c.estimated,
    memory: keep.get(c.id)?.memory ?? null,
  }));
  const areas = areasOf(raw);
  const focus = focusOf(people, areas);
  return { ...s, candidates: scored, areas, focus: focus ? { ...focus, label: s.focus?.label ?? null } : null };
}
